import React from "react";
import { Button, ButtonProps } from "../ui/button";
import { Copy, Check } from "lucide-react";
import { useFlashVisibility } from "../hooks/flash-visibility";
import { cn } from "../../lib/utils";

interface CopyButtonProps {
    text: string;
    label: string;
    confirmedLabel?: string;
    transform?: (text: string) => string;
    htmlTransform?: (text: string) => string;
    variant?: ButtonProps["variant"];
    size?: ButtonProps["size"];
    className?: string;
}

export function CopyButton({
    text,
    label,
    confirmedLabel = "Copied!",
    transform,
    htmlTransform,
    variant = "outline",
    size = "sm",
    className,
}: CopyButtonProps) {
    const [confirming, showConfirmation] = useFlashVisibility();

    function handleCopy() {
        showConfirmation();
        const plainText = transform ? transform(text) : text;

        if (htmlTransform && typeof ClipboardItem !== "undefined") {
            const htmlBlob = new Blob([htmlTransform(text)], { type: "text/html" });
            const textBlob = new Blob([plainText], { type: "text/plain" });
            const data = new ClipboardItem({
                "text/html": htmlBlob,
                "text/plain": textBlob,
            });
            void navigator.clipboard.write([data]);
        } else {
            void navigator.clipboard.writeText(plainText);
        }
    }

    return (
        <Button
            variant={variant}
            size={size}
            className={cn("gap-2 transition-colors", confirming && "border-confirmation bg-confirmation/20", className)}
            onClick={handleCopy}
        >
            {confirming ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            {confirming ? confirmedLabel : label}
        </Button>
    );
}
